import { CardActions, CardContent, DialogContent, DialogTitle } from '@mui/material';
import { useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { FC, useEffect, useState } from 'react';
import { usePatientProfile } from '../../../../hooks/use-patient-profile';
import { ContactApi } from '../../../../services/api-client/contact-api';
import { PatientNote } from '../../../../services/api-client/contact-api.types';
import { PrimaryButton, SecondaryButton } from '../../buttons';
import { Card, CardTitle } from '../../card/card.style';
import { Dialog } from '../../dialog/dialog.styles';
import { CloseDialogButton, DialogContentWrapper, NoteText } from './add-note.styles';

export interface AddNoteProps {
  opened: boolean;
  note?: PatientNote;
  onClose: () => void;
  onAddNode: () => void;
  onUpdateNote: (note: PatientNote) => void;
}

export const AddNoteDialog: FC<AddNoteProps> = (props) => {
  const { opened, note, onClose, onAddNode, onUpdateNote } = props;
  const { getClaims } = useOidcWithAdmin();
  const { showError } = useNotification();
  const { contactDetails, locationId } = usePatientProfile();
  const [text, setText] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const contactApi = ContactApi.getInstance();

  useEffect(() => {
    setText(note?.note || '');
  }, [note]);

  const handleSave = async () => {
    setIsLoading(true);

    try {
      const authorId = (await getClaims()).id;

      if (note) {
        await contactApi.updateNote(note.id, locationId, text, authorId);
        onUpdateNote({ ...note, note: text });
      } else {
        await contactApi.createNote(contactDetails.id, locationId, text, authorId);
        onAddNode();
      }
    } catch (e) {
      showError('Unable to save note.');
      handleClose();
    }

    setIsLoading(false);
  };

  const handleClose = () => {
    setText('');
    onClose();
  };

  return (
    <>
      <Dialog open={opened} maxWidth={false}>
        <DialogContentWrapper>
          <DialogTitle sx={{ cursor: 'default' }}>
            {contactDetails.firstName} {contactDetails.lastName}
            <CloseDialogButton onClick={handleClose} />
          </DialogTitle>
          <DialogContent>
            <Card>
              <CardContent>
                <CardTitle sx={{ paddingLeft: '5px' }}>{`${note ? 'Edit' : 'Add'} Note`}</CardTitle>
                This note is for use inside of RevenueWell only. You will not see it appear in your PMS system.
                <NoteText
                  value={text}
                  label="Note"
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 400 }}
                  onChange={(e: any) => setText(e.target.value)}
                />
                <CardActions>
                  <SecondaryButton
                    onClick={handleClose}
                    color="primary"
                    variant="outlined"
                    disableElevation
                    sx={{ marginRight: '10px' }}
                  >
                    CANCEL
                  </SecondaryButton>
                  <PrimaryButton
                    loading={isLoading}
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                    disableElevation
                    disabled={text.trim().length === 0}
                  >
                    SAVE
                  </PrimaryButton>
                </CardActions>
              </CardContent>
            </Card>
          </DialogContent>
        </DialogContentWrapper>
      </Dialog>
    </>
  );
};
